import { assertNonNullable, requireNonNullable } from '@disy/lib/custom-error';

import { getColorConfiguration } from 'cadenza/config/color-config';
import { couplingLayerExceededNotification } from 'cadenza/map/custom/workbook/workbook-map-view-notification';
import { AttributeStatus, attributeStatusMessage } from 'cadenza/repository/model/attribute-status.gen';
import type { RootStateOr } from 'cadenza/store/cadenza-store';
import type { ActiveAttributeId } from 'cadenza/workbook/api/active-attribute-id';
import type { DataViewDto } from 'cadenza/workbook/api/data-view-dto';
import { DataViewVisualizationKey } from 'cadenza/workbook/api/data-view-visualization-key';
import type { LayerId } from 'cadenza/workbook/api/layer-id';
import type { Objecttype } from 'cadenza/workbook/api/objecttype';
import type { ViewDto } from 'cadenza/workbook/api/view-dto';
import type { ViewId } from 'cadenza/workbook/api/view-id';
import type { WorkbookAttributeDto } from 'cadenza/workbook/api/workbook-attribute-dto';
import type { WorkbookLayerDto } from 'cadenza/workbook/api/workbook-layer-dto';
import { isEnrichmentOutdatedAndRerunnable } from 'cadenza/workbook/enrichment/enrichment-selectors.state';
import { isDataViewReferenceByViewDto } from 'cadenza/workbook/map/workbook-layer-utils';
import type { AttributeBundleName } from 'cadenza/workbook/model/attribute-bundle-name';
import type { AttributeId } from 'cadenza/workbook/model/attribute-id.gen';
import { toDataViewId } from 'cadenza/workbook/model/data-view-id-utils';
import type { DataViewId } from 'cadenza/workbook/model/data-view-id.gen';
import type {
  DataViewNotification,
  DataViewNotificationType,
  WorkbookNotification,
  WorkbookNotificationId,
} from 'cadenza/workbook/notifications/workbook-notifications';
import { createNotification } from 'cadenza/workbook/notifications/workbook-notifications';
import {
  getMapCouplingGroupWithStateById,
  getMapViewSrs,
  isCouplingLayerLimitExceededForGroupByViewId,
} from 'cadenza/workbook/state/workbook-map-view-selectors.state';
import { collectClassificationAttributeErrors } from 'cadenza/workbook/view-validation/color-classification-validation';
import {
  getAttributeById,
  getDataViewById,
  getNotificationsForLayer,
  getNotificationsForView,
  getObjecttypeByAttributeIdMapping,
  getObjecttypeById,
} from 'cadenza/workbook/workbook-core-selectors.state';
import type { WorkbookState } from 'cadenza/workbook/workbook.state';

/**
 * This function is called from the updateWorkbookState function in 'workbook.state', in the context of reducers
 * executed on initial workbook load and when updating the state after executing a workbook command.
 * So the state that is passed in is the state being updated and thus is mutable and can be modified.
 *
 * In this function, the  views state, in particular the data views and layers, are checked for known
 * possible invalid state, or error statuses populated by the backend, and notifications are added to the state for each problem found.
 *
 * UI components that need to display the notifications (e.g. layer in the designer's layer tree) can
 * subscribe to the notifications state.
 *
 * @param state The workbook state being updated (mutable)
 */
export function validateViews(state: WorkbookState) {
  const views = state.viewById;
  Object.values(views).forEach((view) => {
    const newNotifications = [...validateDataViews(view, state.dataViewById, state.activeAttributeById, state)];
    validateMapCouplingGroupLayerCount(newNotifications, state, view);
    addViewNotifications(newNotifications, state, view);
  });
}

export function validateMapViewLayers(state: WorkbookState) {
  const views = state.viewById;
  Object.values(views)
    .filter((view) => view.type === 'MAP')
    .forEach((view) => {
      const newNotifications = [...validateLayers(state, view)];
      addViewNotifications(newNotifications, state, view);
    });
}

function addViewNotifications(newNotifications: WorkbookNotification[], state: WorkbookState, view: ViewDto) {
  const newNotificationsById: Record<WorkbookNotificationId, WorkbookNotification> = {};
  for (const notification of newNotifications) {
    newNotificationsById[notification.id] = notification;
  }

  if (!state.notificationsByView[view.id]) {
    state.notificationsByView[view.id] = {};
  }

  updateNotifications(state.notificationsByView[view.id], newNotificationsById);
}

function updateNotifications(
  currentNotificationsById: Record<WorkbookNotificationId, WorkbookNotification>,
  newNotificationsById: Record<WorkbookNotificationId, WorkbookNotification>,
) {
  // remove old notifications (only the ones added from this source)
  Object.values(currentNotificationsById).forEach((notification) => {
    if (notification.source === 'WORKBOOK_UPDATE') {
      delete currentNotificationsById[notification.id];
    }
  });

  // add the new ones
  Object.assign(currentNotificationsById, newNotificationsById);
}

function validateDataViews(
  view: ViewDto,
  dataViewsById: Record<DataViewId, DataViewDto>,
  activeAttributesById: Record<ActiveAttributeId, WorkbookAttributeDto>,
  state: WorkbookState,
): WorkbookNotification[] {
  const objecttypeByAttributeId = getObjecttypeByAttributeIdMapping(state);
  return view.dataViews.flatMap((dataViewId: DataViewId) =>
    validateDataView(view, dataViewsById[dataViewId], activeAttributesById, objecttypeByAttributeId, state),
  );
}

function validateDataView(
  view: ViewDto,
  dataView: DataViewDto,
  activeAttributesById: Record<ActiveAttributeId, WorkbookAttributeDto>,
  objecttypeByAttributeId: Record<AttributeId, Objecttype>,
  state: WorkbookState,
): WorkbookNotification[] {
  return [
    ...collectAttributeErrors(view.id, dataView, activeAttributesById),
    ...collectClassificationAttributeErrors(view.id, dataView, activeAttributesById, objecttypeByAttributeId),
    ...checkMissingAttributes(state, view, dataView),
    ...checkUnknownVisualization(view, dataView),
    ...validateColorPaletteAndGradient(view.id, dataView, state),
    ...checkForOutdatedEnrichments(state, view.id, dataView),
  ];
}

function collectAttributeErrors(
  viewId: ViewId,
  dataView: DataViewDto,
  activeAttributesById: Record<ActiveAttributeId, WorkbookAttributeDto>,
) {
  if (dataView.status === 'INVALID_FROM_NOT_QUERY_RELEVANT_BUNDLE') {
    return [];
  }
  return Object.values(dataView.attributes)
    .flat()
    .map((activeAttributeId) => activeAttributesById[activeAttributeId])
    .flatMap((attribute) =>
      attribute.status === 'OK' ? [] : [createInvalidAttributeNotification(viewId, dataView.id, attribute.status)],
    );
}

/**
 * Look for attribute bundle that don't meet the minimum attribute count requirements.
 */
function checkMissingAttributes(state: WorkbookState, view: ViewDto, dataView: DataViewDto): WorkbookNotification[] {
  const hasMissingAttribute = Object.values(dataView.attributeBundleDefinitions).some(
    (bundleDefinition) => !areRequiredAttributesFulfilled(state, view, dataView, bundleDefinition.attributeBundleName),
  );

  if (hasMissingAttribute && dataView.status !== 'INVALID_FROM_NOT_QUERY_RELEVANT_BUNDLE') {
    return [
      createNotification('WARNING', 'WORKBOOK_UPDATE', 'MISSING_ATTRIBUTE', view.id, {
        dataViewId: dataView.id,
      }),
    ];
  } else {
    return [];
  }
}

function createInvalidAttributeNotification(
  viewId: ViewId,
  dataViewId: DataViewId,
  attributeStatus: Exclude<AttributeStatus, 'OK'>,
): DataViewNotification {
  const notificationType: DataViewNotificationType = `INVALID_ATTRIBUTE.${attributeStatus}`;

  return createNotification('ERROR', 'WORKBOOK_UPDATE', notificationType, viewId, {
    title: AttributeStatus.i18n(attributeStatus),
    detail: attributeStatusMessage(attributeStatus),
    dataViewId,
  }) as DataViewNotification;
}

/**
 * Check if the minimum number of attributes is satisfied for the given bundle in the given data view.
 */
export function areRequiredAttributesFulfilled(
  state: RootStateOr<WorkbookState>,
  viewDto: ViewDto,
  dataView: DataViewDto,
  bundleName: AttributeBundleName,
) {
  const minNumOfAttributes = dataView.attributeBundleDefinitions[bundleName]?.minNumberOfAttributes ?? 0;
  const numSelectedAttributes = dataView.attributes[bundleName]?.length ?? 0;
  const allAttributesSet = numSelectedAttributes >= minNumOfAttributes;
  if (!allAttributesSet && viewDto.type === 'MAP') {
    // in case we have a referenced dataView layer with an incompatible objecttype there is no
    // "fulfilled" state the user can configure by himself here, and we don't show the notification.
    const layerId = dataView.settings['DATA_VIEW_ID'] as LayerId;
    if (layerId && isDataViewReferenceByViewDto(viewDto, layerId)) {
      const notifications = getNotificationsForLayer(state, viewDto.id, layerId);
      return !notifications.some((n) => n.type === 'LAYER_REFERENCE_INCOMPATIBLE_OBJECTTYPE');
    }
  }
  return allAttributesSet;
}

/**
 * Look for the "unknown visualization" status on data views.
 * @param view
 * @param dataView
 */
function checkUnknownVisualization(view: ViewDto, dataView: DataViewDto): WorkbookNotification[] {
  // Suppress the message on 'UNKNOWN_VISUALISATION' for views of type 'EXTENSION'
  // This is necessary because dynamic visualisations can become unavailable the
  // problem should only be advertised on the view cover, not in the notifications.
  if (view.type !== 'EXTENSION' && dataView.status === 'UNKNOWN_VISUALIZATION') {
    return [
      createNotification('ERROR', 'WORKBOOK_UPDATE', 'UNKNOWN_VISUALIZATION', view.id, {
        dataViewId: dataView.id,
      }),
    ];
  }

  return [];
}

const NAMES_OF_BUNDLES_CONTAINING_COLOR_PALETTES_FOR_VISUALIZATION: Partial<
  Record<DataViewVisualizationKey, AttributeBundleName[]>
> = {
  [DataViewVisualizationKey.CHART.HUNDRED_PERCENT_COLUMN_CHART]: ['DIMENSION'],
  [DataViewVisualizationKey.CHART.GROUPED_BAR_CHART]: ['DIMENSION'],
  [DataViewVisualizationKey.CHART.GROUPED_COLUMN_CHART]: ['DIMENSION'],
  [DataViewVisualizationKey.CHART.STACKED_COLUMN_CHART]: ['DIMENSION'],
  [DataViewVisualizationKey.CHART.MULTI_LINE_CHART]: ['DIMENSION'],
  [DataViewVisualizationKey.CHART.STACKED_BAR_CHART]: ['DIMENSION'],
  [DataViewVisualizationKey.CHART.HUNDRED_PERCENT_BAR_CHART]: ['DIMENSION'],
  [DataViewVisualizationKey.CHART.MIRROR_BAR_CHART]: ['DIMENSION'],
  [DataViewVisualizationKey.CHART.PIE_CHART]: ['DIMENSION'],
  [DataViewVisualizationKey.CHART.MULTI_LINE_CHART]: ['DIMENSION'],
  [DataViewVisualizationKey.CHART.STACKED_AREA_CHART]: ['DIMENSION'],
  [DataViewVisualizationKey.MAP.ROUTES]: ['COLOR_CLASSIFICATION'],
  [DataViewVisualizationKey.MAP.POLYGON]: ['COLOR_CLASSIFICATION'],
  [DataViewVisualizationKey.MAP.POINT]: ['COLOR_CLASSIFICATION'],
  [DataViewVisualizationKey.MAP.LINE]: ['COLOR_CLASSIFICATION'],
  [DataViewVisualizationKey.MAP.CLUSTERING_POINT]: ['COLOR_CLASSIFICATION'],
  [DataViewVisualizationKey.MAP.MAP_PIE_CHART]: ['PIE_CHART_DIMENSION'],
};

const NAMES_OF_BUNDLES_CONTAINING_GRADIENTS_FOR_VISUALIZATION: Partial<
  Record<DataViewVisualizationKey, AttributeBundleName[]>
> = {
  [DataViewVisualizationKey.MAP.POLYGON]: ['COLOR_CLASSIFICATION'],
  [DataViewVisualizationKey.MAP.POINT]: ['COLOR_CLASSIFICATION'],
  [DataViewVisualizationKey.MAP.LINE]: ['COLOR_CLASSIFICATION'],
  [DataViewVisualizationKey.MAP.CLUSTERING_POINT]: ['COLOR_CLASSIFICATION'],
  [DataViewVisualizationKey.MAP.MAP_PIE_CHART]: ['PIE_CHART_MEASURE'],
};

/**
 * Look for invalid color palettes and color gradients in attribute bundles that use them.
 */
function validateColorPaletteAndGradient(
  viewId: ViewId,
  dataView: DataViewDto,
  state: WorkbookState,
): WorkbookNotification[] {
  const notifications: WorkbookNotification[] = [];

  if (
    (NAMES_OF_BUNDLES_CONTAINING_COLOR_PALETTES_FOR_VISUALIZATION[dataView.visualizationKey] || []).some((bundleName) =>
      isColorPaletteInvalidForLastAttributeInTheBundle(dataView, bundleName, state),
    )
  ) {
    notifications.push(
      createNotification('WARNING', 'WORKBOOK_UPDATE', 'INVALID_COLOR_PALETTE', viewId, {
        dataViewId: dataView.id,
      }),
    );
  }

  if (
    (NAMES_OF_BUNDLES_CONTAINING_GRADIENTS_FOR_VISUALIZATION[dataView.visualizationKey] || []).some((bundleName) =>
      isGradientInvalidForLastAttributeInTheBundle(dataView, bundleName, state),
    )
  ) {
    notifications.push(
      createNotification('WARNING', 'WORKBOOK_UPDATE', 'INVALID_COLOR_GRADIENT', viewId, {
        dataViewId: dataView.id,
      }),
    );
  }

  return notifications;
}

function checkForOutdatedEnrichments(
  state: WorkbookState,
  viewId: ViewId,
  dataView: DataViewDto,
): WorkbookNotification[] {
  const enrichmentsUsedByDataView = Object.values(dataView.attributes)
    .flat()
    .map((attributeId) => getAttributeById(state, attributeId))
    .filter((attribute) => Boolean(attribute.enrichedObjecttypeId))
    .map((attribute) => {
      const enrichedObjecttype = getObjecttypeById(state, attribute.enrichedObjecttypeId!);
      assertNonNullable(enrichedObjecttype.enrichments);
      const enrichment = enrichedObjecttype.enrichments.find(
        (anEnrichment) => anEnrichment.objecttypeId === attribute.objecttypeId,
      );
      return requireNonNullable(enrichment, `Enrichment for attribute ${attribute} not found`);
    });
  return enrichmentsUsedByDataView
    .filter((enrichment) => isEnrichmentOutdatedAndRerunnable(state, enrichment))
    .map((enrichment) =>
      createNotification('WARNING', 'WORKBOOK_UPDATE', 'USED_ENRICHMENT_OUTDATED', viewId, {
        actionParams: { enrichment },
        detailParams: { enrichment: getObjecttypeById(state, enrichment.objecttypeId).printName },
        additionalGroupingKey: enrichment.objecttypeId,
      }),
    );
}

/**
 * Check whether the last attribute of the bundle contains invalid colorPaletteId.
 *
 * @param dataView
 * @param bundleName - Bundle name that contains attribute that has colorPalette set
 * @param state
 * @param colorConfiguration
 * @return is invalid color palette on the last attribute of the bundle
 */
function isColorPaletteInvalidForLastAttributeInTheBundle(
  dataView: DataViewDto,
  bundleName: AttributeBundleName,
  state: WorkbookState,
) {
  const attributes = dataView.attributes[bundleName];
  if (!attributes?.length) {
    return false;
  }
  const lastAttributeId = attributes[attributes.length - 1];
  const { colorPaletteId } = getAttributeById(state, lastAttributeId);
  const availableColorPalettesIds = Object.keys(getColorConfiguration().colorPalettesById);
  return !!colorPaletteId && !isValidColorPaletteId(availableColorPalettesIds, colorPaletteId);
}

function isValidColorPaletteId(availableColorPalettesIds: string[], colorPaletteId: string) {
  return availableColorPalettesIds.includes(colorPaletteId);
}

/**
 * Check whether the last attribute of the bundle contains invalid gradientId.
 *
 * @param dataView
 * @param bundleName - Bundle name that contains attribute that has gradient set
 * @param state
 * @param colorConfiguration
 * @return is invalid color palette on the last attribute of the bundle
 */
function isGradientInvalidForLastAttributeInTheBundle(
  dataView: DataViewDto,
  bundleName: AttributeBundleName,
  state: WorkbookState,
) {
  const attributes = dataView.attributes[bundleName];
  if (!attributes?.length) {
    return false;
  }
  const lastAttributeId = attributes[attributes.length - 1];
  const { colorGradientId } = getAttributeById(state, lastAttributeId);
  const availableGradientsIds = Object.keys(getColorConfiguration().gradientsById);
  return !!colorGradientId && !availableGradientsIds.includes(colorGradientId);
}

function validateLayers(state: WorkbookState, view: ViewDto): WorkbookNotification[] {
  const mapSrs = getMapViewSrs(state, view.id);
  return Object.values(view.layerDefinitions).flatMap((layerDefinition) => [
    ...getNotificationsForView(state, view.id),
    ...collectBackendLayerError(view.id, layerDefinition),
    ...checkLayerSrs(state, view.id, layerDefinition.id, mapSrs),
  ]);
}

function collectBackendLayerError(viewId: ViewId, layer: WorkbookLayerDto): WorkbookNotification[] {
  return layer.status === 'OK' ?
      []
    : [
        createNotification('ERROR', 'WORKBOOK_UPDATE', layer.status, viewId, {
          layerId: layer.id,
        }),
      ];
}

/**
 * Check if the given layer's SRS is compatible with the SRS assigned to the view that contains it.
 */
function checkLayerSrs(
  state: WorkbookState,
  viewId: ViewId,
  layerId: LayerId,
  mapSrs: string | undefined,
): WorkbookNotification[] {
  const dataView = getDataViewById(state, toDataViewId(layerId));

  if (dataView?.status !== 'VALID' && dataView?.status !== 'INVALID_FROM_NOT_QUERY_RELEVANT_BUNDLE') {
    // only perform check for valid data views
    return [];
  }

  const layerSrsDiffersFromMap = dataView.attributes['GEOMETRY']?.some((attributeId) => {
    const attribute = getAttributeById(state, attributeId);
    return attribute.relatedGeometryType === 'RASTER' && attribute.srs && attribute.srs !== mapSrs;
  });

  return layerSrsDiffersFromMap ?
      [
        createNotification('WARNING', 'WORKBOOK_UPDATE', 'LAYER_SRS_DIFFERS_FROM_MAP', viewId, {
          layerId,
        }),
      ]
    : [];
}

function validateMapCouplingGroupLayerCount(
  notifications: WorkbookNotification[],
  state: WorkbookState,
  view: ViewDto,
) {
  const couplingGroupId = view.mapViewContext?.mapCouplingGroupId;
  if (view.type === 'MAP' && couplingGroupId) {
    const group = getMapCouplingGroupWithStateById(state, couplingGroupId);
    if (isCouplingLayerLimitExceededForGroupByViewId(state, view.id)) {
      notifications.push(couplingLayerExceededNotification(view.id));
      group.initiator = undefined;
      group.isLayerLimitExceeded = true;
    } else if (group.isLayerLimitExceeded) {
      group.isLayerLimitExceeded = false;
    }
  }
}
